#!/usr/bin/env node
import { execFile } from "node:child_process";
import fs from "node:fs/promises";
import { realpathSync } from "node:fs";
import path from "node:path";
import { stdin as input, stdout as output } from "node:process";
import { pathToFileURL } from "node:url";
import { promisify } from "node:util";
import { Command, Option } from "commander";
import JSON5 from "json5";
import pc from "picocolors";
import {
  beginOpenAiBrowserOAuth,
  completeOpenAiDeviceCode,
  deleteOpenAiOAuthCredential,
  ensureWorkspace,
  loadConfig,
  listHooks,
  McpClient,
  openAiOAuthStatus,
  ProviderRegistry,
  requestOpenAiDeviceCode,
  resolveSecret,
  saveOpenAiOAuthCredential,
  setHookEnabled,
  type ChannelAccountConfig,
  validateConfig,
  writeConfig,
} from "@mewbleh/nori-core";
import { GatewayClient, Logger, NoriGateway } from "@mewbleh/nori-gateway";
import { AgentStore, agentDatabasePath, ensureNoriDirectories, resolveNoriPaths, SecretVault, type CronSchedule } from "@mewbleh/nori-persistence";
import { PROTOCOL_VERSION } from "@mewbleh/nori-protocol";
import { createContext, ensureGatewayToken, gatewayUrl, resolveGatewayToken } from "./context.js";
import { createBackup, restoreBackup, verifyBackup } from "./backup.js";
import { serveAcpGateway } from "./acp-server.js";
import { migrateOpenClaw } from "./migrate.js";
import { serveMcpGateway } from "./mcp-server.js";
import {
  CHANNEL_ENV,
  PROVIDER_ENV,
  runInteractiveOnboarding,
  type ModelDiscoveryRequest,
  type OnboardingSelection,
} from "./onboarding.js";
import { auditSecurity, fixSecurity } from "./security.js";
import { installService, serviceAction, uninstallService } from "./service.js";
import { explainSandbox, listNoriSandboxes, recreateNoriSandboxes } from "./sandbox.js";
import { runUpdate, updateStatus } from "./update.js";
import {
  promptWasCancelled,
  renderNoriBanner,
  requireInteractiveTui,
  tuiConfirm,
  tuiInput,
  tuiNote,
  tuiSearch,
  tuiSelect,
} from "./tui.js";

const VERSION = "0.3.0";
const execFileAsync = promisify(execFile);
const program = new Command();
program
  .name("nori")
  .description("Nori — your headless puppy-girl AI companion 🐶")
  .version(VERSION)
  .option("--profile <name>", "use an isolated ~/.nori-<name> profile")
  .option("--json", "emit machine-readable JSON")
  .option("--no-color", "disable terminal colors");

function print(value: unknown): void {
  if (program.opts().json || typeof value !== "string") output.write(`${JSON.stringify(value, null, 2)}\n`);
  else output.write(`${value}\n`);
}

function profile(): string | undefined {
  return program.opts().profile as string | undefined;
}

function cliPaths() {
  const activeProfile = profile();
  return resolveNoriPaths(activeProfile ? { profile: activeProfile } : {});
}

async function withClient<T>(callback: (client: GatewayClient) => Promise<T>): Promise<T> {
  const paths = cliPaths();
  const config = await loadConfig(paths);
  const token = process.env.NORI_GATEWAY_TOKEN ?? process.env.OPENCLAW_GATEWAY_TOKEN ?? await resolveSecret(config.gateway.auth.token);
  if (!token) throw new Error("Gateway token is not configured. Run `nori setup`.");
  const client = new GatewayClient(gatewayUrl(config), token, VERSION);
  await client.connect();
  try {
    return await callback(client);
  } finally {
    client.close();
  }
}

async function runLocalAgent(message: string, options: { session?: string; model?: string }, stream = true): Promise<string> {
  const context = await createContext(profile());
  try {
    let text = "";
    for await (const event of context.runtime.run({
      text: message,
      sessionKey: options.session ?? "agent:main:cli",
      ...(options.model ? { model: options.model } : {}),
    })) {
      if (event.type === "text-delta") {
        text += event.text;
        if (stream && !program.opts().json) output.write(event.text);
      } else if (event.type === "error") throw new Error(event.message);
    }
    if (stream && !program.opts().json) output.write("\n");
    return text;
  } finally {
    await context.close();
  }
}

async function openExternalUrl(url: string): Promise<boolean> {
  const command = process.platform === "darwin" ? "open" : process.platform === "win32" ? "rundll32.exe" : "xdg-open";
  const args = process.platform === "win32" ? ["url.dll,FileProtocolHandler", url] : [url];
  try {
    await execFileAsync(command, args, { timeout: 10_000, windowsHide: true });
    return true;
  } catch {
    return false;
  }
}

async function loginOpenAi(context: Awaited<ReturnType<typeof createContext>>, method: "oauth" | "device-code", openBrowser = true): Promise<unknown> {
  if (method === "device-code") {
    const device = await requestOpenAiDeviceCode();
    if (program.opts().json) print({ event: "openai.device-code", verificationUrl: device.verificationUrl, userCode: device.userCode, expiresAt: device.expiresAt });
    else output.write(`Open ${device.verificationUrl}\nEnter code: ${device.userCode}\n\nContinue only if you started this login in Nori. The code expires in 15 minutes.\n`);
    const credential = await completeOpenAiDeviceCode(device);
    saveOpenAiOAuthCredential(context.agentStore, credential);
    context.config.providers.openai = { ...context.config.providers.openai, enabled: true, authMode: "oauth" };
    await writeConfig(context.paths, context.config);
    return openAiOAuthStatus(context.agentStore);
  }
  const flow = await beginOpenAiBrowserOAuth();
  const opened = openBrowser ? await openExternalUrl(flow.authorizationUrl) : false;
  if (program.opts().json) print({ event: "openai.oauth-url", authorizationUrl: flow.authorizationUrl, redirectUri: flow.redirectUri, opened });
  else output.write(`${opened ? "Complete sign-in in your browser." : `Open this URL to sign in:\n${flow.authorizationUrl}`}\n`);
  const credential = await flow.complete;
  saveOpenAiOAuthCredential(context.agentStore, credential);
  context.config.providers.openai = { ...context.config.providers.openai, enabled: true, authMode: "oauth" };
  await writeConfig(context.paths, context.config);
  return openAiOAuthStatus(context.agentStore);
}

function parseDuration(value: string): number {
  const match = /^(\d+(?:\.\d+)?)(ms|s|m|h|d)$/iu.exec(value.trim());
  if (!match) throw new Error("Duration must use ms, s, m, h, or d (for example: 30m)");
  const units: Record<string, number> = { ms: 1, s: 1_000, m: 60_000, h: 3_600_000, d: 86_400_000 };
  const milliseconds = Number(match[1]) * units[match[2]!.toLowerCase()]!;
  if (!Number.isSafeInteger(milliseconds) || milliseconds < 1_000) throw new Error("Duration must be at least one second");
  return milliseconds;
}

function scheduleFromOptions(options: { at?: string; every?: string; cron?: string }): CronSchedule | undefined {
  const selected = [options.at, options.every, options.cron].filter((value) => value !== undefined);
  if (selected.length > 1) throw new Error("Choose only one of --at, --every, or --cron");
  if (options.at) {
    const atMs = Date.parse(options.at);
    if (!Number.isFinite(atMs)) throw new Error("--at must be an ISO-8601 date/time");
    return { kind: "at", atMs };
  }
  if (options.every) return { kind: "every", everyMs: parseDuration(options.every) };
  if (options.cron) return { kind: "cron", expr: options.cron, tz: "UTC" };
  return undefined;
}

function showNoriBanner(): void {
  renderNoriBanner({ write: (value) => output.write(value), color: program.opts().color !== false });
}

function shortDate(value: number): string {
  return Number.isFinite(value) ? new Date(value).toLocaleString() : "unknown time";
}

async function selectModelTui(options: { persist: boolean; banner?: boolean }): Promise<string | undefined> {
  requireInteractiveTui(Boolean(program.opts().json));
  if (options.banner !== false) showNoriBanner();
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try {
    const current = context.config.agents.defaults.model.primary;
    const separator = current.indexOf("/");
    const currentProvider = separator > 0 ? current.slice(0, separator) : "openai";
    const registered = [...context.providers.providers.keys()];
    if (!registered.includes(currentProvider)) registered.unshift(currentProvider);
    const configured = new Set(Object.entries(context.config.providers).filter(([, value]) => value?.enabled !== false).map(([id]) => id));
    const provider = await tuiSelect("Choose a model provider", registered.map((id) => ({
      value: id,
      name: `${id}${configured.has(id) || id === "ollama" ? "" : " (not configured)"}`,
      ...(id === currentProvider ? { description: "current provider" } : {}),
    })), currentProvider);

    let discovered: Array<{ id: string; displayName?: string }> = [];
    if (!configured.has(provider) && !["ollama", "lmstudio", "vllm"].includes(provider)) {
      tuiNote(pc.yellow(`${provider} is not configured yet, so enter its model ID manually.`));
    } else {
      try {
        tuiNote(`Discovering models from ${provider}…`);
        discovered = await context.runtime.listModels(provider);
      } catch (error) {
        tuiNote(pc.yellow(`Live discovery was unavailable: ${(error as Error).message}`));
      }
    }
    const currentId = current.startsWith(`${provider}/`) ? current.slice(provider.length + 1) : undefined;
    if (currentId && !discovered.some((model) => model.id === currentId)) discovered.unshift({ id: currentId, displayName: "Current model" });
    const custom = "__nori_custom_model__";
    const choice = await tuiSearch("Search for a model", [
      ...discovered.map((model) => ({
        value: model.id,
        name: model.displayName ? `${model.displayName} (${model.id})` : model.id,
        ...(model.id === currentId ? { description: "current model" } : {}),
      })),
      { value: custom, name: "Enter a model ID manually", description: "Use a model that discovery did not return" },
    ], currentId && discovered.some((model) => model.id === currentId) ? currentId : undefined);
    const modelId = choice === custom
      ? (await tuiInput("Model ID", { validate: (value) => value.trim().length > 0 || "Enter a model ID" })).trim()
      : choice;
    const selected = `${provider}/${modelId}`;
    if (options.persist) {
      context.config.agents.defaults.model.primary = selected;
      await writeConfig(context.paths, context.config);
      tuiNote(`${pc.green("✔")} Default model saved as ${pc.bold(selected)}.`);
    }
    return selected;
  } catch (error) {
    if (promptWasCancelled(error)) return undefined;
    throw error;
  } finally {
    await context.close();
  }
}

type SessionSummary = {
  sessionKey: string;
  label?: string;
  agentId: string;
  updatedAt: number;
  archived: boolean;
  model?: string;
};

async function browseSessionsTui(options: { banner?: boolean } = {}): Promise<void> {
  requireInteractiveTui(Boolean(program.opts().json));
  if (options.banner !== false) showNoriBanner();
  try {
    await withClient(async (client) => {
      while (true) {
        const [activeResult, archivedResult] = await Promise.all([
          client.request("sessions.list", { limit: 100 }),
          client.request("sessions.list", { limit: 100, archived: true }),
        ]) as [{ sessions: SessionSummary[] }, { sessions: SessionSummary[] }];
        const all = [...activeResult.sessions, ...archivedResult.sessions];
        if (!all.length) {
          tuiNote("No saved sessions yet. Start one with `nori chat`.");
          return;
        }
        const back = "__back__";
        const key = await tuiSearch("Find a conversation", [
          ...all.map((session) => ({
            value: session.sessionKey,
            name: `${session.label || session.sessionKey}${session.archived ? " [archived]" : ""}`,
            description: `${session.agentId} • ${shortDate(session.updatedAt)}${session.model ? ` • ${session.model}` : ""}`,
          })),
          { value: back, name: "Back" },
        ]);
        if (key === back) return;
        const selected = all.find((session) => session.sessionKey === key)!;
        const action = await tuiSelect("Conversation action", [
          { value: "view", name: "View transcript" },
          { value: "rename", name: "Rename" },
          { value: selected.archived ? "unarchive" : "archive", name: selected.archived ? "Unarchive" : "Archive" },
          { value: "reset", name: "Reset context", description: "Preserves the conversation record but starts fresh context" },
          { value: "delete", name: "Delete permanently" },
          { value: "back", name: "Back" },
        ]);
        if (action === "back") continue;
        if (action === "view") {
          const detail = await client.request("sessions.get", { key, limit: 100 }) as { messages: Array<{ role: string; content: unknown; createdAt: number }> };
          tuiNote(detail.messages.length ? detail.messages.map((message) => {
            const content = typeof message.content === "string" ? message.content : JSON.stringify(message.content);
            return `${pc.bold(message.role)} · ${shortDate(message.createdAt)}\n${content}`;
          }).join("\n\n") : "This session has no transcript messages.");
        } else if (action === "rename") {
          const label = (await tuiInput("Conversation label", {
            ...(selected.label ? { defaultValue: selected.label } : {}),
            validate: (value) => value.trim().length > 0 || "Enter a label",
          })).trim();
          await client.request("sessions.patch", { key, label });
          tuiNote(`${pc.green("✔")} Renamed to ${label}.`);
        } else if (action === "archive" || action === "unarchive") {
          await client.request("sessions.patch", { key, archived: action === "archive" });
          tuiNote(`${pc.green("✔")} Conversation ${action}d.`);
        } else if (action === "reset") {
          if (await tuiConfirm(`Reset context for ${selected.label || key}?`, false)) {
            await client.request("sessions.reset", { key });
            tuiNote(`${pc.green("✔")} Conversation context reset.`);
          }
        } else if (action === "delete") {
          if (await tuiConfirm(`Permanently delete ${selected.label || key} and its transcript?`, false)) {
            await client.request("sessions.delete", { key, deleteTranscript: true });
            tuiNote(`${pc.green("✔")} Conversation deleted.`);
          }
        }
      }
    });
  } catch (error) {
    if (!promptWasCancelled(error)) throw error;
  }
}

async function reviewApprovalsTui(options: { banner?: boolean } = {}): Promise<void> {
  requireInteractiveTui(Boolean(program.opts().json));
  if (options.banner !== false) showNoriBanner();
  try {
    await withClient(async (client) => {
      while (true) {
        const result = await client.request("approvals.list", { status: "pending" }) as {
          approvals: Array<{ id: string; kind: string; summary: string; context: unknown; createdAt: number }>;
        };
        if (!result.approvals.length) {
          tuiNote(`${pc.green("✔")} There are no pending approvals.`);
          return;
        }
        const back = "__back__";
        const id = await tuiSearch("Review a pending action", [
          ...result.approvals.map((approval) => ({
            value: approval.id,
            name: `${approval.kind}: ${approval.summary}`,
            description: shortDate(approval.createdAt),
          })),
          { value: back, name: "Back" },
        ]);
        if (id === back) return;
        const approval = result.approvals.find((entry) => entry.id === id)!;
        tuiNote(`${pc.bold(approval.summary)}\n${JSON.stringify(approval.context, null, 2)}`);
        const decision = await tuiSelect("Decision", [
          { value: "approved", name: "Approve once" },
          { value: "denied", name: "Deny" },
          { value: "back", name: "Back" },
        ]);
        if (decision === "back") continue;
        if (!await tuiConfirm(`${decision === "approved" ? "Approve" : "Deny"} this action?`, false)) continue;
        await client.request("approvals.resolve", { id, decision });
        tuiNote(`${pc.green("✔")} Action ${decision}.`);
      }
    });
  } catch (error) {
    if (!promptWasCancelled(error)) throw error;
  }
}

async function reviewPairingsTui(options: { banner?: boolean } = {}): Promise<void> {
  requireInteractiveTui(Boolean(program.opts().json));
  if (options.banner !== false) showNoriBanner();
  try {
    await withClient(async (client) => {
      while (true) {
        const result = await client.request("pairing.list") as {
          pairings: Array<{ channel: string; account_id: string; sender_id: string; code: string; approved_at: number | null; expires_at: number }>;
        };
        const pending = result.pairings.filter((entry) => !entry.approved_at && entry.expires_at > Date.now());
        if (!pending.length) {
          tuiNote(`${pc.green("✔")} There are no pending sender pairings.`);
          return;
        }
        const back = "__back__";
        const code = await tuiSearch("Choose a sender to pair", [
          ...pending.map((entry) => ({
            value: entry.code,
            name: `${entry.channel} · ${entry.sender_id}`,
            description: `code ${entry.code} • expires ${shortDate(entry.expires_at)}`,
          })),
          { value: back, name: "Back" },
        ]);
        if (code === back) return;
        const pairing = pending.find((entry) => entry.code === code)!;
        if (!await tuiConfirm(`Allow ${pairing.sender_id} to talk to Nori on ${pairing.channel}?`, false)) continue;
        const approved = await client.request("pairing.approve", { code }) as { approved: boolean };
        tuiNote(approved.approved ? `${pc.green("✔")} Sender paired.` : pc.yellow("That pairing code expired before it could be approved."));
      }
    });
  } catch (error) {
    if (!promptWasCancelled(error)) throw error;
  }
}

async function managePluginsTui(options: { banner?: boolean } = {}): Promise<void> {
  requireInteractiveTui(Boolean(program.opts().json));
  if (options.banner !== false) showNoriBanner();
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try {
    while (true) {
      const installed = context.sharedStore.listPlugins();
      const install = "__install__";
      const back = "__back__";
      const id = await tuiSearch("Manage plugins", [
        ...installed.map((plugin) => ({
          value: plugin.id,
          name: `${plugin.id}${plugin.enabled ? " [enabled]" : " [disabled]"}`,
          description: plugin.packageName,
        })),
        { value: install, name: "Install a plugin package" },
        { value: back, name: "Back" },
      ]);
      if (id === back) return;
      if (id === install) {
        const packageName = (await tuiInput("npm package name", { validate: (value) => value.trim().length > 0 || "Enter a package name" })).trim();
        const manifest = await context.pluginManager.inspect(packageName);
        tuiNote(`${pc.bold(String(manifest.id))}\n${JSON.stringify(manifest, null, 2)}`);
        if (!await tuiConfirm(`Install ${packageName}?`, false)) continue;
        const enable = await tuiConfirm("Enable and allow this plugin immediately?", false);
        const installedManifest = await context.pluginManager.install(packageName, enable);
        if (enable && !context.config.plugins.allow.includes(installedManifest.id)) context.config.plugins.allow.push(installedManifest.id);
        await writeConfig(context.paths, context.config);
        tuiNote(`${pc.green("✔")} Installed ${installedManifest.id}${enable ? " and enabled it" : ""}.`);
        continue;
      }
      const plugin = installed.find((entry) => entry.id === id)!;
      const action = await tuiSelect("Plugin action", [
        { value: "inspect", name: "Inspect manifest" },
        { value: plugin.enabled ? "disable" : "enable", name: plugin.enabled ? "Disable" : "Enable and allow" },
        { value: "uninstall", name: "Uninstall" },
        { value: "back", name: "Back" },
      ]);
      if (action === "back") continue;
      if (action === "inspect") {
        tuiNote(JSON.stringify(plugin.manifest, null, 2));
      } else if (action === "enable" || action === "disable") {
        const enabled = action === "enable";
        context.sharedStore.setPluginEnabled(id, enabled);
        context.config.plugins.allow = enabled
          ? [...new Set([...context.config.plugins.allow, id])]
          : context.config.plugins.allow.filter((entry) => entry !== id);
        await writeConfig(context.paths, context.config);
        tuiNote(`${pc.green("✔")} ${id} ${enabled ? "enabled" : "disabled"}; restart the gateway to apply.`);
      } else if (action === "uninstall" && await tuiConfirm(`Uninstall ${id}?`, false)) {
        await context.pluginManager.uninstall(id);
        context.config.plugins.allow = context.config.plugins.allow.filter((entry) => entry !== id);
        await writeConfig(context.paths, context.config);
        tuiNote(`${pc.green("✔")} ${id} uninstalled.`);
      }
    }
  } catch (error) {
    if (!promptWasCancelled(error)) throw error;
  } finally {
    await context.close();
  }
}

async function createCronTui(options: { banner?: boolean } = {}): Promise<void> {
  requireInteractiveTui(Boolean(program.opts().json));
  if (options.banner !== false) showNoriBanner();
  try {
    const config = await loadConfig(cliPaths());
    const name = (await tuiInput("Job name", { validate: (value) => value.trim().length > 0 || "Enter a job name" })).trim();
    const message = (await tuiInput("What should Nori do?", { validate: (value) => value.trim().length > 0 || "Enter an agent message" })).trim();
    const kind = await tuiSelect("Schedule", [
      { value: "every", name: "Repeat at an interval", description: "For example, every 30 minutes" },
      { value: "cron", name: "Cron expression", description: "Five-field expression in UTC" },
      { value: "at", name: "Run once", description: "ISO-8601 date and time" },
    ]);
    const scheduleValue = (await tuiInput(kind === "every" ? "Interval" : kind === "cron" ? "Cron expression (UTC)" : "Run at (ISO-8601)", {
      defaultValue: kind === "every" ? "1h" : kind === "cron" ? "0 9 * * *" : new Date(Date.now() + 3_600_000).toISOString(),
    })).trim();
    const schedule = scheduleFromOptions(kind === "every" ? { every: scheduleValue } : kind === "cron" ? { cron: scheduleValue } : { at: scheduleValue })!;
    const agentId = await tuiSelect("Agent", config.agents.list.map((agent) => ({ value: agent.id, name: agent.id })), "main");
    const sessionKey = (await tuiInput("Session key (optional)", { defaultValue: `agent:${agentId}:cron:${name.toLowerCase().replace(/[^a-z0-9]+/gu, "-").replace(/^-|-$/gu, "") || "job"}` })).trim();
    const enabled = await tuiConfirm("Enable this job now?", true);
    tuiNote(`${pc.bold(name)}\n${message}\n${JSON.stringify(schedule)}\nAgent: ${agentId}\nSession: ${sessionKey || "automatic"}\nEnabled: ${enabled ? "yes" : "no"}`);
    if (!await tuiConfirm("Create this scheduled job?", true)) return;
    await withClient(async (client) => {
      const result = await client.request("cron.add", { name, message, schedule, agentId, ...(sessionKey ? { sessionKey } : {}), enabled });
      tuiNote(`${pc.green("✔")} Scheduled job created.\n${JSON.stringify(result, null, 2)}`);
    });
  } catch (error) {
    if (!promptWasCancelled(error)) throw error;
  }
}

async function mainMenu(): Promise<void> {
  requireInteractiveTui(Boolean(program.opts().json));
  showNoriBanner();
  while (true) {
    try {
      const choice = await tuiSelect("What would you like to do?", [
        { value: "chat", name: "Chat with Nori", description: "Start an interactive terminal conversation" },
        { value: "model", name: "Choose the default model" },
        { value: "sessions", name: "Browse conversations" },
        { value: "approvals", name: "Review pending approvals" },
        { value: "pairing", name: "Review sender pairings" },
        { value: "plugins", name: "Manage plugins" },
        { value: "cron", name: "Create a scheduled job" },
        { value: "quit", name: "Quit" },
      ]);
      if (choice === "quit") return;
      if (choice === "chat") await tui({ banner: false });
      else if (choice === "model") await selectModelTui({ persist: true, banner: false });
      else if (choice === "sessions") await browseSessionsTui({ banner: false });
      else if (choice === "approvals") await reviewApprovalsTui({ banner: false });
      else if (choice === "pairing") await reviewPairingsTui({ banner: false });
      else if (choice === "plugins") await managePluginsTui({ banner: false });
      else if (choice === "cron") await createCronTui({ banner: false });
    } catch (error) {
      if (promptWasCancelled(error)) return;
      tuiNote(pc.red((error as Error).message));
    }
  }
}

async function discoverSetupModels(request: ModelDiscoveryRequest, store: AgentStore) {
  if (request.authMethod === "oauth" || request.authMethod === "device-code") return [];
  let apiKey: string | undefined;
  if (request.credential.source === "encrypted") apiKey = request.credential.secret ?? store.getCredential(request.provider);
  if (request.credential.source === "env") apiKey = process.env[request.credential.name];
  if (request.credential.source === "file") apiKey = await resolveSecret({ source: "file", path: request.credential.path });
  const local = ["ollama", "lmstudio", "vllm"].includes(request.provider);
  if (request.authMethod === "api-key" && !apiKey && !local) {
    throw new Error("The selected credential is not available yet. You can still enter a model ID manually.");
  }

  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 15_000);
  const discoveryFetch: typeof globalThis.fetch = (resource, init) => globalThis.fetch(resource, { ...init, signal: init?.signal ?? controller.signal });
  try {
    const provider = new ProviderRegistry().get(request.provider);
    return await provider.listModels({
      fetch: discoveryFetch,
      ...(apiKey ? { apiKey, auth: { type: "api-key" as const } } : {}),
      ...(request.baseUrl ? { baseUrl: request.baseUrl } : {}),
    });
  } catch (error) {
    if (timedOut) throw new Error(`Model discovery timed out for ${request.provider}`);
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

function channelIsEnabled(channel: unknown): boolean {
  if (!channel || typeof channel !== "object") return false;
  const value = channel as { enabled?: boolean; accounts?: Record<string, { enabled?: boolean }> };
  if (value.accounts) return Object.values(value.accounts).some((account) => account.enabled !== false);
  return value.enabled === true;
}

function setInitialChannelEnabled(config: Awaited<ReturnType<typeof loadConfig>>, id: string, enabled: boolean): void {
  const current = config.channels[id];
  const accounts = current && "accounts" in current
    ? (current as { accounts: Record<string, ChannelAccountConfig> }).accounts
    : undefined;
  if (accounts) {
    const entries = Object.entries(accounts);
    const anyEnabled = entries.some(([, account]) => account.enabled !== false);
    config.channels[id] = {
      ...current,
      accounts: Object.fromEntries(entries.map(([accountId, account], index) => [
        accountId,
        { ...account, enabled: enabled ? (anyEnabled ? account.enabled !== false : index === 0) : false },
      ])),
    };
    return;
  }
  if (!current && !enabled) return;
  const account = (current ?? {}) as ChannelAccountConfig;
  config.channels[id] = {
    ...account,
    enabled,
    dmPolicy: account.dmPolicy ?? "pairing",
    groupPolicy: account.groupPolicy ?? "allowlist",
    ...(enabled && CHANNEL_ENV[id] && !account.token ? { token: { source: "env", name: CHANNEL_ENV[id] } } : {}),
  };
}

const setup = program.command("setup").alias("onboard").description("configure Nori without a web UI");
setup
  .option("--non-interactive", "require all choices through flags and environment")
  .option("--provider <id>", "model provider")
  .addOption(new Option("--auth-method <method>", "provider authentication method").choices(["api-key", "oauth", "device-code"]))
  .option("--model <provider/model>", "default model")
  .option("--channel <id...>", "channels to configure")
  .option("--install-daemon", "install an operating-system service")
  .action(async (options) => {
    const interactive = !options.nonInteractive;
    if (interactive && (program.opts().json || !input.isTTY || !output.isTTY)) {
      throw Object.assign(new Error("Interactive setup requires a terminal. Run `nori setup` in a TTY, or use `nori setup --non-interactive --provider <id> --model <provider/model>`."), { code: "INTERACTIVE_TTY_REQUIRED" });
    }
    const paths = cliPaths();
    await ensureNoriDirectories(paths);
    const setupVault = new SecretVault(paths.masterKeyPath);
    await setupVault.initialize();
    const config = await loadConfig(paths);
    const setupStore = new AgentStore(agentDatabasePath(paths, "main"), setupVault);

    let selection: OnboardingSelection | undefined;
    try {
      if (interactive) {
        showNoriBanner();
        const configuredChannels = ["telegram", "whatsapp", "discord"].filter((id) => channelIsEnabled(config.channels[id]));
        const currentModel = ((options.model as string | undefined) ?? config.agents.defaults.model.primary) || undefined;
        const currentProvider = (options.provider as string | undefined) ?? currentModel?.split("/")[0] ?? "openai";
        const existingProviders = Object.fromEntries(Object.keys(PROVIDER_ENV).map((id) => {
          const providerConfig = config.providers[id];
          return [id, {
            ...(providerConfig?.baseUrl ? { baseUrl: providerConfig.baseUrl } : {}),
            hasEncryptedCredential: Boolean(setupStore.getCredential(id)),
            ...(providerConfig?.apiKey?.source === "env" ? { env: providerConfig.apiKey.name } : {}),
            ...(providerConfig?.apiKey?.source === "file" ? { file: providerConfig.apiKey.path } : {}),
          }];
        }));
        selection = await runInteractiveOnboarding({
          preferredProvider: currentProvider,
          ...(options.authMethod ? { preferredAuthMethod: options.authMethod as "api-key" | "oauth" | "device-code" } : {}),
          ...(currentModel ? { preferredModel: currentModel } : {}),
          selectedChannels: options.channel?.length ? options.channel : configuredChannels,
          installDaemon: Boolean(options.installDaemon),
          existingProviders,
          discoverModels: (request) => discoverSetupModels(request, setupStore),
          write: (message) => output.write(message),
        });
        if (!selection) {
          output.write(`${pc.yellow("Setup cancelled.")} Your existing configuration was not changed.\n`);
          return;
        }
      } else {
        let provider = (options.provider as string | undefined) ?? "openai";
        let model = options.model as string | undefined;
        if (!model) throw new Error("A default --model provider/model is required in non-interactive setup");
        if (!model.includes("/")) model = `${provider}/${model}`;
        provider = model.split("/")[0]!;
        const authMethod = (options.authMethod ?? "api-key") as "api-key" | "oauth" | "device-code";
        if (provider !== "openai" && authMethod !== "api-key") throw new Error(`${authMethod} authentication is only supported for the OpenAI provider`);
        const local = ["ollama", "lmstudio", "vllm"].includes(provider);
        selection = {
          provider,
          authMethod: local ? "none" : authMethod,
          credential: local || authMethod !== "api-key"
            ? { source: "none" }
            : { source: "env", name: PROVIDER_ENV[provider] ?? `${provider.toUpperCase()}_API_KEY` },
          model,
          channels: (options.channel ?? []) as string[],
          installDaemon: Boolean(options.installDaemon),
        };
      }

      const { provider, model, channels } = selection;
      if (!model.startsWith(`${provider}/`)) throw new Error(`Model ${model} does not belong to provider ${provider}`);
      for (const channel of channels) if (!["telegram", "whatsapp", "discord"].includes(channel)) throw new Error(`Unsupported initial channel: ${channel}`);
      config.agents.defaults.model.primary = model;
      const previousProvider = config.providers[provider] ?? {};
      const providerConfig = {
        enabled: true,
        ...(previousProvider.oauthBaseUrl ? { oauthBaseUrl: previousProvider.oauthBaseUrl } : {}),
        ...(selection.baseUrl ? { baseUrl: selection.baseUrl } : {}),
        ...(selection.authMethod === "oauth" || selection.authMethod === "device-code" ? { authMode: "oauth" as const } : {}),
        ...(selection.authMethod === "api-key" ? { authMode: "api-key" as const } : {}),
        ...(selection.credential.source === "env" ? { apiKey: { source: "env" as const, name: selection.credential.name } } : {}),
        ...(selection.credential.source === "file" ? { apiKey: { source: "file" as const, path: selection.credential.path } } : {}),
      };
      config.providers[provider] = providerConfig;
      if (selection.credential.source === "encrypted" && selection.credential.secret) setupStore.setCredential(provider, "default", selection.credential.secret);
      if (selection.credential.source !== "encrypted") setupStore.deleteCredential(provider);
      const channelIds = interactive ? ["telegram", "whatsapp", "discord"] : channels;
      for (const channel of channelIds) setInitialChannelEnabled(config, channel, channels.includes(channel));

      await ensureGatewayToken(paths, config);
      await ensureWorkspace(paths.workspaceDir);
      await writeConfig(paths, config);
    } finally {
      setupStore.close();
    }

    if (!selection) return;
    const { provider, model, channels } = selection;
    const useOpenAiOAuth = provider === "openai" && (selection.authMethod === "oauth" || selection.authMethod === "device-code");
    const result: Record<string, unknown> = { config: paths.configPath, workspace: paths.workspaceDir, model, channels };
    if (useOpenAiOAuth) {
      const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
      try { result.openaiAuth = await loginOpenAi(context, selection.authMethod === "device-code" ? "device-code" : "oauth"); }
      finally { await context.close(); }
    }
    if (selection.installDaemon) result.service = await installService();
    if (interactive) {
      output.write(`\n${pc.green("✔ Nori is ready.")}\n${pc.dim(`  Model: ${model}\n  Config: ${paths.configPath}`)}\n`);
      if (channels.includes("whatsapp")) output.write(`${pc.cyan("Next:")} run ${pc.bold("nori channels login whatsapp")} to scan the QR code.\n`);
      output.write(`${pc.cyan("Chat:")} ${pc.bold("nori chat")}   ${pc.cyan("Gateway:")} ${pc.bold("nori gateway run")}\n`);
    } else {
      print(result);
    }
  });

program
  .command("agent")
  .description("run one agent turn")
  .requiredOption("-m, --message <text>", "message to Nori")
  .option("--session <key>", "session key", "agent:main:cli")
  .option("--model <provider/model>", "override the configured model")
  .option("--local", "run in this process instead of through the gateway")
  .action(async (options) => {
    if (options.local) {
      const text = await runLocalAgent(options.message, options, !program.opts().json);
      if (program.opts().json) print({ text });
      return;
    }
    const result = await withClient(async (client) => {
      const started = (await client.request("agent", { message: options.message, sessionKey: options.session, model: options.model })) as { runId: string };
      return client.request("agent.wait", { runId: started.runId }, 10 * 60_000);
    });
    print(result);
  });

async function tui(options: { banner?: boolean } = {}): Promise<void> {
  requireInteractiveTui(Boolean(program.opts().json));
  if (options.banner !== false) showNoriBanner();
  let session = "agent:main:tui";
  let model = (await loadConfig(cliPaths())).agents.defaults.model.primary;
  tuiNote(`Session: ${pc.bold(session)} • Model: ${pc.bold(model)}\nType ${pc.bold("/help")} for chat commands.`);
  try {
    while (true) {
      const message = (await tuiInput("you")).trim();
      if (!message) continue;
      if (message === "/exit" || message === "/quit") break;
      if (message === "/help") {
        tuiNote("/model — choose a model for this chat\n/session [key] — switch conversation\n/clear — clear the terminal\n/exit — leave chat");
        continue;
      }
      if (message === "/model") {
        const selected = await selectModelTui({ persist: false, banner: false });
        if (selected) {
          model = selected;
          tuiNote(`Using ${pc.bold(model)} for this chat.`);
        }
        continue;
      }
      if (message === "/session" || message.startsWith("/session ")) {
        const requested = message.slice("/session".length).trim();
        session = requested || (await tuiInput("Session key", {
          defaultValue: session,
          validate: (value) => value.trim().length > 0 || "Enter a session key",
        })).trim();
        tuiNote(`Switched to ${pc.bold(session)}.`);
        continue;
      }
      if (message === "/clear") {
        output.write("\u001B[2J\u001B[H");
        showNoriBanner();
        continue;
      }
      if (message.startsWith("/")) {
        tuiNote(pc.yellow(`Unknown chat command: ${message.split(/\s/u)[0]}. Type /help.`));
        continue;
      }
      output.write(pc.magenta("nori> "));
      await runLocalAgent(message, { session, model });
    }
  } catch (error) {
    if (!promptWasCancelled(error)) throw error;
  }
}

for (const name of ["tui", "chat", "terminal"]) program.command(name).description("chat with Nori in the terminal").action(() => tui());
program.command("menu").alias("interactive").description("open Nori's interactive terminal control center").action(mainMenu);

program.command("acp").description("serve an Agent Client Protocol bridge over stdio")
  .option("--url <gateway-url>", "Nori Gateway WebSocket URL")
  .option("--token-file <path>", "read the Gateway token from a private file")
  .option("--session <key>", "attach new ACP sessions to a fixed Gateway session key")
  .option("--no-prefix-cwd", "do not prefix prompts with the ACP working directory")
  .action(async (options) => {
    const paths = cliPaths();
    const config = await loadConfig(paths);
    const token = options.tokenFile
      ? (await fs.readFile(path.resolve(options.tokenFile), "utf8")).trim()
      : process.env.NORI_GATEWAY_TOKEN ?? process.env.OPENCLAW_GATEWAY_TOKEN ?? await resolveSecret(config.gateway.auth.token);
    if (!token) throw new Error("Gateway token is unavailable; pass --token-file or configure gateway.auth.token");
    await serveAcpGateway({
      url: options.url ?? gatewayUrl(config),
      token,
      ...(options.session ? { sessionKey: options.session } : {}),
      prefixCwd: Boolean(options.prefixCwd),
    });
  });

const gateway = program.command("gateway").description("run and manage the Nori gateway");
gateway.command("run").description("run the gateway in the foreground").action(async () => {
  const context = await createContext(profile());
  const server = new NoriGateway({
    config: context.config,
    paths: context.paths,
    runtime: context.runtime,
    agentStore: context.agentStore,
    agentStores: context.agentStores,
    sharedStore: context.sharedStore,
    providers: context.providers,
    authToken: await resolveGatewayToken(context),
    channels: context.channels,
    logger: new Logger(context.paths.logsDir),
    version: VERSION,
  });
  await server.start();
  await context.channels.start().catch((error: unknown) => process.stderr.write(`Channel startup warning: ${(error as Error).message}\n`));
  let stopping = false;
  const stop = (signal: string) => {
    if (stopping) return;
    stopping = true;
    void (async () => {
      try {
        await server.stop(signal);
      } catch (error) {
        process.stderr.write(`Gateway shutdown error: ${(error as Error).message}\n`);
        process.exitCode = 1;
      } finally {
        await context.close().catch((error: unknown) => {
          process.stderr.write(`Context shutdown error: ${(error as Error).message}\n`);
          process.exitCode = 1;
        });
      }
    })();
  };
  process.once("SIGINT", () => stop("SIGINT"));
  process.once("SIGTERM", () => stop("SIGTERM"));
});
gateway.command("status").action(async () => print(await withClient((client) => client.request("status"))));
gateway.command("call").argument("<method>").option("--params <json>", "JSON/JSON5 parameters", "{}").action(async (method, options) => {
  print(await withClient((client) => client.request(method, JSON5.parse(options.params))));
});
gateway.command("install").action(async () => print({ service: await installService() }));
gateway.command("uninstall").description("remove the operating-system service without deleting Nori data").action(async () => print({ removed: await uninstallService(), dataPreserved: true }));
for (const action of ["start", "stop", "restart"] as const) gateway.command(action).action(async () => { await serviceAction(action); print({ ok: true, action }); });

const daemon = program.command("daemon").description("manage the Nori gateway operating-system service");
daemon.command("install").action(async () => print({ service: await installService() }));
daemon.command("uninstall").description("remove the operating-system service without deleting Nori data").action(async () => print({ removed: await uninstallService(), dataPreserved: true }));
for (const action of ["start", "stop", "restart"] as const) daemon.command(action).action(async () => { await serviceAction(action); print({ ok: true, action }); });

program.command("uninstall").description("remove the Nori gateway service while preserving configuration and data")
  .action(async () => print({ removed: await uninstallService(), dataPreserved: true }));

program.command("status").action(async () => print(await withClient((client) => client.request("status"))));
program.command("health").action(async () => print(await withClient((client) => client.request("health"))));
program.command("logs").option("--lines <number>", "number of lines", "100").action(async (options) => print(await withClient((client) => client.request("logs.tail", { lines: Number(options.lines) }))));

const models = program.command("models");
models.command("list").action(async () => {
  const context = await createContext(profile(), { initializeMcp: false });
  try {
    const providers = [];
    for (const id of context.providers.providers.keys()) {
      const configured = Boolean(context.config.providers[id]) || id === "ollama";
      if (!configured) providers.push({ id, configured, models: [] });
      else {
        try { providers.push({ id, configured, models: await context.runtime.listModels(id) }); }
        catch (error) { providers.push({ id, configured, models: [], error: (error as Error).message }); }
      }
    }
    print({ providers });
  } finally { await context.close(); }
});
models.command("select").description("interactively choose and save the default model")
  .action(async () => { await selectModelTui({ persist: true }); });
models.command("probe").argument("<provider>").action(async (provider) => print(await withClient((client) => client.request("models.probe", { provider }))));
const modelAuth = models.command("auth").description("manage model-provider authentication");
modelAuth.command("status", { isDefault: true }).option("--provider <id>", "provider id", "openai").option("--agent <id>", "agent id", "main").action(async (options) => {
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try {
    if (options.provider !== "openai") throw new Error("OAuth status is currently supported for openai");
    print(openAiOAuthStatus(context.storeForAgent(String(options.agent))));
  } finally { await context.close(); }
});
modelAuth.command("login").option("--provider <id>", "provider id", "openai")
  .addOption(new Option("--method <method>", "login method").choices(["oauth", "device-code"]).default("oauth"))
  .option("--device-code", "use Codex-compatible headless device authorization")
  .option("--no-open", "print the browser OAuth URL without opening it")
  .action(async (options) => {
    if (options.provider !== "openai") throw new Error("OAuth login is currently supported for openai");
    const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
    try { print(await loginOpenAi(context, options.deviceCode ? "device-code" : options.method, options.open)); }
    finally { await context.close(); }
  });
modelAuth.command("logout").option("--provider <id>", "provider id", "openai").option("--agent <id>", "agent id", "main").action(async (options) => {
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try {
    if (options.provider !== "openai") throw new Error("OAuth logout is currently supported for openai");
    print({ provider: "openai", method: "oauth", removed: deleteOpenAiOAuthCredential(context.storeForAgent(String(options.agent))) });
  } finally { await context.close(); }
});

const memoryCommand = program.command("memory").description("inspect and maintain durable vector memory");
memoryCommand.command("status", { isDefault: true }).option("--agent <id>", "agent id", "main")
  .action(async (options) => print(await withClient((client) => client.request("memory.status", { agentId: options.agent }))));
memoryCommand.command("search").argument("<query>").option("--limit <number>", "result count", "10").option("--agent <id>", "agent id", "main")
  .action(async (query, options) => print(await withClient((client) => client.request("memory.search", { query, limit: Number(options.limit), agentId: options.agent }))));
memoryCommand.command("reindex").option("--agent <id>", "agent id", "main")
  .action(async (options) => print(await withClient((client) => client.request("memory.reindex", { agentId: options.agent }))));

const mcpCommand = program.command("mcp").description("serve MCP and manage outbound MCP servers");
mcpCommand.command("serve")
  .option("--url <gateway-url>", "Nori Gateway WebSocket URL")
  .option("--token-file <path>", "read the Gateway token from a private file")
  .option("--verbose", "write diagnostics to stderr")
  .action(async (options) => {
    const paths = cliPaths();
    const config = await loadConfig(paths);
    const token = options.tokenFile
      ? (await fs.readFile(path.resolve(options.tokenFile), "utf8")).trim()
      : process.env.NORI_GATEWAY_TOKEN ?? process.env.OPENCLAW_GATEWAY_TOKEN ?? await resolveSecret(config.gateway.auth.token);
    if (!token) throw new Error("Gateway token is unavailable; pass --token-file or configure gateway.auth.token");
    await serveMcpGateway({ url: options.url ?? gatewayUrl(config), token, verbose: Boolean(options.verbose) });
  });
mcpCommand.command("list", { isDefault: true }).action(async () => {
  const paths = cliPaths();
  const config = await loadConfig(paths);
  print({ servers: Object.entries(config.mcp.servers).map(([name, server]) => ({ name, ...server })) });
});
mcpCommand.command("show").argument("<name>").action(async (name) => {
  const config = await loadConfig(cliPaths());
  const server = config.mcp.servers[name];
  if (!server) throw new Error(`Unknown MCP server: ${name}`);
  print({ name, server });
});
mcpCommand.command("status").action(async () => {
  const context = await createContext(profile(), { loadPlugins: false });
  try { print({ servers: context.runtime.mcpStatus() }); }
  finally { await context.close(); }
});
mcpCommand.command("doctor").action(async () => {
  const paths = cliPaths();
  const config = await loadConfig(paths);
  const errors = validateConfig(config).filter((error) => error.startsWith("mcp."));
  const context = await createContext(profile(), { loadPlugins: false });
  try { print({ ok: errors.length === 0 && context.runtime.mcpStatus().every((server) => !config.mcp.servers[server.name]?.enabled || server.connected), errors, servers: context.runtime.mcpStatus() }); }
  finally { await context.close(); }
});
mcpCommand.command("add").argument("<name>")
  .option("--command <command>", "stdio server executable")
  .option("--arg <value...>", "stdio argument(s)")
  .option("--url <url>", "streamable HTTP endpoint")
  .option("--cwd <path>", "stdio working directory")
  .option("--disabled", "save without enabling")
  .action(async (name, options) => {
    if (Boolean(options.command) === Boolean(options.url)) throw new Error("Choose exactly one of --command or --url");
    const paths = cliPaths();
    const config = await loadConfig(paths);
    if (config.mcp.servers[name]) throw new Error(`MCP server already exists: ${name}`);
    config.mcp.servers[name] = options.command
      ? { transport: "stdio", command: options.command, args: options.arg ?? [], ...(options.cwd ? { cwd: path.resolve(options.cwd) } : {}), enabled: !options.disabled }
      : { transport: "streamable-http", url: options.url, enabled: !options.disabled };
    await writeConfig(paths, config);
    print({ name, server: config.mcp.servers[name], restartRequired: true });
  });
const setMcpServer = async (name: string, raw: string) => {
  const paths = cliPaths();
  const config = await loadConfig(paths);
  const server = JSON5.parse(raw) as Record<string, unknown>;
  if (!server || typeof server !== "object" || Array.isArray(server)) throw new Error("MCP server config must be a JSON object");
  config.mcp.servers[name] = server;
  await writeConfig(paths, config);
  print({ name, server: config.mcp.servers[name], restartRequired: true });
};
mcpCommand.command("set").argument("<name>").argument("<json>").action(setMcpServer);
mcpCommand.command("configure").argument("<name>").argument("<json>").action(setMcpServer);
mcpCommand.command("unset").argument("<name>").action(async (name) => {
  const paths = cliPaths();
  const config = await loadConfig(paths);
  const removed = Boolean(config.mcp.servers[name]);
  delete config.mcp.servers[name];
  await writeConfig(paths, config);
  print({ name, removed, restartRequired: true });
});
for (const subcommand of ["probe", "tools"] as const) mcpCommand.command(subcommand).argument("<name>").action(async (name) => {
  const config = await loadConfig(cliPaths());
  const server = config.mcp.servers[name];
  if (!server) throw new Error(`Unknown MCP server: ${name}`);
  const client = await McpClient.connect(name, server);
  try { print({ name, ok: true, tools: await client.listTools() }); }
  finally { client.close(); }
});
mcpCommand.command("reload").action(async () => print(await withClient((client) => client.request("mcp.reload"))));

const cronCommand = program.command("cron").description("manage durable scheduled agent jobs");
cronCommand.command("list", { isDefault: true }).option("--limit <number>", "job count", "100").option("--enabled", "list only enabled jobs")
  .action(async (options) => print(await withClient((client) => client.request("cron.list", { limit: Number(options.limit), ...(options.enabled ? { enabled: true } : {}) }))));
cronCommand.command("status").action(async () => print(await withClient((client) => client.request("cron.status"))));
cronCommand.command("get").argument("<id>").action(async (id) => print(await withClient((client) => client.request("cron.get", { id }))));
cronCommand.command("add")
  .requiredOption("--name <name>", "job name")
  .requiredOption("--message <text>", "message sent to the agent")
  .option("--at <datetime>", "run once at an ISO-8601 date/time")
  .option("--every <duration>", "repeat at an interval such as 30m")
  .option("--cron <expression>", "five-field UTC cron expression")
  .option("--agent <id>", "agent id", "main")
  .option("--session <key>", "stable session key")
  .option("--disabled", "create the job disabled")
  .action(async (options) => {
    const schedule = scheduleFromOptions(options);
    if (!schedule) throw new Error("One of --at, --every, or --cron is required");
    print(await withClient((client) => client.request("cron.add", {
      name: options.name,
      message: options.message,
      schedule,
      agentId: options.agent,
      ...(options.session ? { sessionKey: options.session } : {}),
      enabled: !options.disabled,
    })));
  });
cronCommand.command("create").description("create a scheduled job with a guided terminal wizard").action(async () => { await createCronTui(); });
cronCommand.command("update").argument("<id>")
  .option("--name <name>")
  .option("--message <text>")
  .option("--at <datetime>")
  .option("--every <duration>")
  .option("--cron <expression>")
  .option("--enable")
  .option("--disable")
  .action(async (id, options) => {
    if (options.enable && options.disable) throw new Error("Choose only one of --enable or --disable");
    const schedule = scheduleFromOptions(options);
    print(await withClient((client) => client.request("cron.update", {
      id,
      ...(options.name ? { name: options.name } : {}),
      ...(options.message ? { message: options.message } : {}),
      ...(schedule ? { schedule } : {}),
      ...(options.enable || options.disable ? { enabled: Boolean(options.enable) } : {}),
    })));
  });
cronCommand.command("remove").argument("<id>").action(async (id) => print(await withClient((client) => client.request("cron.remove", { id }))));
cronCommand.command("run").argument("<id>").action(async (id) => print(await withClient((client) => client.request("cron.run", { id }))));
cronCommand.command("runs").argument("[id]").option("--limit <number>", "run count", "100")
  .action(async (id, options) => print(await withClient((client) => client.request("cron.runs", { ...(id ? { id } : {}), limit: Number(options.limit) }))));

const tasksCommand = program.command("tasks").description("inspect and cancel durable background tasks");
tasksCommand.command("list", { isDefault: true }).option("--status <status>").option("--limit <number>", "task count", "100")
  .action(async (options) => print(await withClient((client) => client.request("tasks.list", { ...(options.status ? { status: options.status } : {}), limit: Number(options.limit) }))));
tasksCommand.command("get").argument("<id>").action(async (id) => print(await withClient((client) => client.request("tasks.get", { id }))));
tasksCommand.command("cancel").argument("<id>").action(async (id) => print(await withClient((client) => client.request("tasks.cancel", { id }))));

const channels = program.command("channels");
channels.command("status").action(async () => print(await withClient((client) => client.request("channels.status"))));
channels.command("probe").argument("<channel>").option("--account <id>", "account id", "default").action(async (channel, options) => print(await withClient((client) => client.request("channels.probe", { channel, accountId: options.account }))));
channels.command("start").argument("[channel]").action(async (channel) => print(await withClient((client) => client.request("channels.start", { channel }))));
channels.command("stop").argument("[channel]").action(async (channel) => print(await withClient((client) => client.request("channels.stop", { channel }))));
channels.command("login").argument("[channel]").description("log in an interactive channel (currently WhatsApp)").action(async (requestedChannel) => {
  let channel = requestedChannel as string | undefined;
  if (!channel) {
    requireInteractiveTui(Boolean(program.opts().json));
    showNoriBanner();
    channel = await tuiSelect("Choose a channel to log in", [
      { value: "whatsapp", name: "WhatsApp", description: "Scan a QR code from your phone" },
    ]);
  } else {
    requireInteractiveTui(Boolean(program.opts().json));
    showNoriBanner();
  }
  const context = await createContext(profile());
  if (channel !== "whatsapp") throw new Error(`${channel} uses token configuration rather than interactive login`);
  try {
    await context.channels.start("whatsapp");
    tuiNote("Scan the QR code above. Press Ctrl+C when WhatsApp is connected.");
    await new Promise<void>((resolve) => process.once("SIGINT", resolve));
  } finally {
    await context.close();
  }
});
channels.command("logout").argument("<channel>").option("--account <id>", "account id", "default").action(async (channel, options) => print(await withClient((client) => client.request("channels.logout", { channel, accountId: options.account }))));

const messageCommand = program.command("message").description("send an approval-gated outbound channel message");
messageCommand.command("send", { isDefault: true })
  .requiredOption("--channel <id>")
  .requiredOption("--to <recipient>")
  .requiredOption("--text <text>")
  .option("--account <id>", "channel account", "default")
  .addOption(new Option("--kind <kind>").choices(["dm", "group", "thread"]).default("dm"))
  .option("--thread <id>")
  .option("--reply-to <id>")
  .option("--confirm", "create or consume a durable approval")
  .option("--idempotency-key <key>", "stable key for approval retry")
  .action(async (options) => print(await withClient((client) => client.request("message.send", {
    channel: options.channel,
    accountId: options.account,
    to: options.to,
    text: options.text,
    kind: options.kind,
    ...(options.thread ? { threadId: options.thread } : {}),
    ...(options.replyTo ? { replyToId: options.replyTo } : {}),
    confirm: Boolean(options.confirm),
    ...(options.idempotencyKey ? { idempotencyKey: options.idempotencyKey } : {}),
  }))));

const pairing = program.command("pairing");
pairing.command("list").action(async () => print(await withClient((client) => client.request("pairing.list"))));
pairing.command("approve").argument("<code>").action(async (code) => print(await withClient((client) => client.request("pairing.approve", { code }))));
pairing.command("review").description("interactively review pending sender pairings").action(async () => { await reviewPairingsTui(); });

const devicesCommand = program.command("devices").description("manage signed device pairing and role tokens");
devicesCommand.command("list", { isDefault: true }).action(async () => print(await withClient((client) => client.request("device.pair.list"))));
devicesCommand.command("approve").argument("<request-id>").action(async (requestId) => print(await withClient((client) => client.request("device.pair.approve", { requestId }))));
devicesCommand.command("reject").argument("<request-id>").action(async (requestId) => print(await withClient((client) => client.request("device.pair.reject", { requestId }))));
devicesCommand.command("remove").argument("<device-id>").action(async (deviceId) => print(await withClient((client) => client.request("device.pair.remove", { deviceId }))));
devicesCommand.command("rotate").requiredOption("--device <id>").requiredOption("--role <role>").option("--scope <scope...>")
  .action(async (options) => print(await withClient((client) => client.request("device.token.rotate", { deviceId: options.device, role: options.role, ...(options.scope ? { scopes: options.scope } : {}) }))));
devicesCommand.command("revoke").requiredOption("--device <id>").requiredOption("--role <role>")
  .action(async (options) => print(await withClient((client) => client.request("device.token.revoke", { deviceId: options.device, role: options.role }))));
devicesCommand.command("clear").requiredOption("--yes", "confirm removal").option("--pending", "also reject pending requests").action(async (options) => {
  const result = await withClient(async (client) => {
    const listed = await client.request("device.pair.list") as { paired: Array<{ deviceId: string }>; pending: Array<{ requestId: string }> };
    for (const device of listed.paired) await client.request("device.pair.remove", { deviceId: device.deviceId });
    if (options.pending) for (const request of listed.pending) await client.request("device.pair.reject", { requestId: request.requestId });
    return { removed: listed.paired.length, rejected: options.pending ? listed.pending.length : 0, existingConnectionsMayRequireDisconnect: true };
  });
  print(result);
});

const nodesCommand = program.command("nodes").description("inspect paired nodes and invoke declared capabilities");
for (const subcommand of ["status", "list"] as const) nodesCommand.command(subcommand, { isDefault: subcommand === "status" })
  .action(async () => print(await withClient((client) => client.request("node.list"))));
nodesCommand.command("describe").requiredOption("--node <id-or-name>").action(async (options) => print(await withClient((client) => client.request("node.describe", { nodeId: options.node }))));
nodesCommand.command("pending").action(async () => print(await withClient((client) => client.request("node.pair.list"))));
nodesCommand.command("approve").argument("<request-id>").action(async (requestId) => print(await withClient((client) => client.request("node.pair.approve", { requestId }))));
nodesCommand.command("reject").argument("<request-id>").action(async (requestId) => print(await withClient((client) => client.request("node.pair.reject", { requestId }))));
nodesCommand.command("remove").requiredOption("--node <id>").action(async (options) => print(await withClient((client) => client.request("node.pair.remove", { deviceId: options.node }))));
nodesCommand.command("rename").requiredOption("--node <id>").requiredOption("--name <display-name>")
  .action(async (options) => print(await withClient((client) => client.request("node.rename", { nodeId: options.node, name: options.name }))));
nodesCommand.command("invoke").requiredOption("--node <id-or-name>").requiredOption("--command <command>").option("--params <json>", "JSON/JSON5 command parameters", "{}")
  .option("--invoke-timeout <ms>", "node timeout", "15000").option("--idempotency-key <key>")
  .action(async (options) => print(await withClient((client) => client.request("node.invoke", {
    nodeId: options.node,
    command: options.command,
    params: JSON5.parse(options.params),
    timeoutMs: Number(options.invokeTimeout),
    ...(options.idempotencyKey ? { idempotencyKey: options.idempotencyKey } : {}),
  }, Number(options.invokeTimeout) + 5_000))));

const sessions = program.command("sessions");
sessions.command("list", { isDefault: true })
  .option("--limit <number>", "session count", "100")
  .option("--offset <number>", "pagination offset", "0")
  .option("--archived", "list archived sessions")
  .option("--search <text>", "filter by key, id, or label")
  .option("--agent <id>", "agent id")
  .action(async (options) => print(await withClient((client) => client.request("sessions.list", {
    limit: Number(options.limit),
    offset: Number(options.offset),
    archived: Boolean(options.archived),
    ...(options.search ? { search: options.search } : {}),
    ...(options.agent ? { agentId: options.agent } : {}),
  }))));
sessions.command("show").argument("<key>").option("--limit <number>", "message count", "100")
  .action(async (key, options) => print(await withClient((client) => client.request("sessions.get", { key, limit: Number(options.limit) }))));
sessions.command("label").argument("<key>").argument("<label>")
  .action(async (key, label) => print(await withClient((client) => client.request("sessions.patch", { key, label }))));
for (const [name, archived] of [["archive", true], ["unarchive", false]] as const) {
  sessions.command(name).argument("<key>").action(async (key) => print(await withClient((client) => client.request("sessions.patch", { key, archived }))));
}
sessions.command("reset").argument("<key>").action(async (key) => print(await withClient((client) => client.request("sessions.reset", { key }))));
sessions.command("compact").argument("<key>").option("--max-lines <number>", "transcript events to preserve", "60")
  .action(async (key, options) => print(await withClient((client) => client.request("sessions.compact", { key, maxLines: Number(options.maxLines) }))));
sessions.command("delete").argument("<key>").option("--archived-only", "refuse to delete an active session")
  .option("--keep-transcript", "retain transcript rows")
  .action(async (key, options) => print(await withClient((client) => client.request("sessions.delete", {
    key,
    archivedOnly: Boolean(options.archivedOnly),
    deleteTranscript: !options.keepTranscript,
  }))));
sessions.command("browse").description("interactively browse and manage conversations").action(async () => { await browseSessionsTui(); });

const transcripts = program.command("transcripts").description("inspect persisted conversation transcripts");
transcripts.command("list", { isDefault: true }).option("--limit <number>", "session count", "100")
  .action(async (options) => print(await withClient((client) => client.request("sessions.list", { limit: Number(options.limit) }))));
transcripts.command("show").argument("<key>").option("--limit <number>", "message count", "100")
  .action(async (key, options) => print(await withClient((client) => client.request("sessions.get", { key, limit: Number(options.limit) }))));
transcripts.command("path").option("--agent <id>", "agent id", "main").action(async (options) => {
  const paths = cliPaths();
  const config = await loadConfig(paths);
  if (!config.agents.list.some((agent) => agent.id === String(options.agent))) throw new Error(`Unknown agent: ${options.agent}`);
  print(agentDatabasePath(paths, String(options.agent)));
});

const toolsCommand = program.command("tools").description("inspect and invoke agent tools");
toolsCommand.command("catalog", { isDefault: true }).option("--agent <id>", "agent id", "main")
  .action(async (options) => print(await withClient((client) => client.request("tools.catalog", { agentId: options.agent }))));
toolsCommand.command("effective").argument("<session-key>").option("--agent <id>", "agent id", "main")
  .action(async (sessionKey, options) => print(await withClient((client) => client.request("tools.effective", { sessionKey, agentId: options.agent }))));
toolsCommand.command("invoke").argument("<name>").option("--args <json>", "JSON/JSON5 tool arguments", "{}")
  .option("--session <key>", "session key", "agent:main:cli")
  .option("--agent <id>", "agent id", "main")
  .option("--confirm", "create or consume a durable approval")
  .option("--idempotency-key <key>", "stable key used to retry an approved request")
  .action(async (name, options) => print(await withClient((client) => client.request("tools.invoke", {
    name,
    args: JSON5.parse(options.args),
    sessionKey: options.session,
    agentId: options.agent,
    confirm: Boolean(options.confirm),
    ...(options.idempotencyKey ? { idempotencyKey: options.idempotencyKey } : {}),
  }))));

const skillsCommand = program.command("skills").description("inspect local workspace skills");
skillsCommand.command("list", { isDefault: true }).option("--agent <id>", "agent id", "main")
  .action(async (options) => print(await withClient((client) => client.request("skills.status", { agentId: options.agent }))));
skillsCommand.command("show").argument("<key>").option("--agent <id>", "agent id", "main")
  .action(async (key, options) => print(await withClient((client) => client.request("skills.skillCard", { skillKey: key, agentId: options.agent }))));
skillsCommand.command("search").argument("[query]", "local name/description query", "").option("--limit <number>", "result count", "25")
  .option("--agent <id>", "agent id", "main")
  .action(async (query, options) => print(await withClient((client) => client.request("skills.search", { query, limit: Number(options.limit), agentId: options.agent }))));

const hooksCommand = program.command("hooks").description("manage bundled event-driven automations");
hooksCommand.command("list", { isDefault: true }).option("--eligible", "show only eligible hooks").option("-v, --verbose", "include event details")
  .action(async (options) => {
    const hooks = listHooks(await loadConfig(cliPaths())).filter((hook) => !options.eligible || hook.eligible);
    print({ hooks: options.verbose ? hooks : hooks.map(({ events: _events, ...hook }) => hook) });
  });
hooksCommand.command("info").argument("<name>").action(async (name) => {
  const hook = listHooks(await loadConfig(cliPaths())).find((entry) => entry.name === name);
  if (!hook) throw new Error(`Unknown hook: ${name}`);
  print({ hook });
});
hooksCommand.command("check").action(async () => {
  const hooks = listHooks(await loadConfig(cliPaths()));
  print({ ok: hooks.every((hook) => hook.eligible), ready: hooks.filter((hook) => hook.eligible).length, total: hooks.length, hooks });
});
for (const [action, enabled] of [["enable", true], ["disable", false]] as const) hooksCommand.command(action).argument("<name>").action(async (name) => {
  const paths = cliPaths();
  const config = await loadConfig(paths);
  setHookEnabled(config, name, enabled);
  await writeConfig(paths, config);
  print({ name, enabled, restartRequired: true });
});

const sandboxCommand = program.command("sandbox").description("inspect Nori-owned execution sandboxes");
sandboxCommand.command("list", { isDefault: true }).action(async () => print(await listNoriSandboxes()));
sandboxCommand.command("explain").option("--agent <id>", "agent id", "main").action(async (options) => {
  const paths = cliPaths();
  print(explainSandbox(await loadConfig(paths), String(options.agent)));
});
sandboxCommand.command("recreate").option("--force", "remove every Docker container labeled ai.nori.sandbox=true")
  .action(async (options) => print(await recreateNoriSandboxes(Boolean(options.force))));

const agents = program.command("agents");
agents.command("list", { isDefault: true }).action(async () => {
  print({ agents: (await loadConfig(cliPaths())).agents.list });
});
agents.command("add").argument("<id>").option("--workspace <path>").option("--model <provider/model>").action(async (id, options) => {
  if (!/^[a-z0-9][a-z0-9_-]{0,63}$/u.test(id)) throw new Error("Invalid agent id");
  const paths = cliPaths();
  const config = await loadConfig(paths);
  if (config.agents.list.some((agent) => agent.id === id)) throw new Error(`Agent already exists: ${id}`);
  const workspace = path.resolve(options.workspace ?? path.join(paths.stateDir, "workspaces", id));
  await ensureWorkspace(workspace);
  const agent = { id, workspace, ...(options.model ? { model: String(options.model) } : {}) };
  config.agents.list.push(agent);
  await writeConfig(paths, config);
  print({ agent });
});
agents.command("remove").argument("<id>").action(async (id) => {
  if (id === "main") throw new Error("The main agent cannot be removed");
  const paths = cliPaths();
  const config = await loadConfig(paths);
  const before = config.agents.list.length;
  config.agents.list = config.agents.list.filter((agent) => agent.id !== id);
  if (before === config.agents.list.length) throw new Error(`Unknown agent: ${id}`);
  await writeConfig(paths, config);
  print({ id, removed: true, retainedState: true });
});
agents.command("set-identity").option("--agent <id>", "agent id", "main").option("--name <name>", "agent name", "Nori").option("--emoji <emoji>", "signature emoji", "🐶").action(async (options) => {
  const config = await loadConfig(cliPaths());
  const agent = config.agents.list.find((entry) => entry.id === options.agent);
  if (!agent) throw new Error(`Unknown agent: ${options.agent}`);
  const workspace = agent.workspace ?? config.agents.defaults.workspace;
  await fs.mkdir(workspace, { recursive: true, mode: 0o700 });
  const filePath = path.join(workspace, "IDENTITY.md");
  await fs.writeFile(filePath, `# Identity\n\n- **Name:** ${options.name}\n- **Creature:** Adult puppy-girl AI companion\n- **Vibe:** Warm, playful, and capable\n- **Emoji:** ${options.emoji}\n`, { mode: 0o600 });
  print({ updated: filePath });
});

const configCommand = program.command("config");
configCommand.command("file").action(() => print(cliPaths().configPath));
configCommand.command("get").argument("[path]").action(async (keyPath) => {
  const paths = cliPaths();
  const config = await loadConfig(paths);
  const value = keyPath ? String(keyPath).split(".").reduce<unknown>((current, key) => (current as Record<string, unknown>)?.[key], config) : config;
  print(value);
});
configCommand.command("set").argument("<path>").argument("<value>").action(async (keyPath, rawValue) => {
  const paths = cliPaths();
  const config = await loadConfig(paths);
  const keys = String(keyPath).split(".");
  if (keys.some((key) => ["__proto__", "prototype", "constructor"].includes(key))) throw new Error("Unsafe configuration path");
  if (/(?:api[_-]?key|token|secret|password)$/iu.test(keys.at(-1)!) && !String(rawValue).trimStart().startsWith("{")) {
    throw new Error("Secrets must be configured with an env/file reference, not a command-line value");
  }
  let target = config as unknown as Record<string, unknown>;
  for (const key of keys.slice(0, -1)) {
    if (!target[key] || typeof target[key] !== "object") target[key] = {};
    target = target[key] as Record<string, unknown>;
  }
  try { target[keys.at(-1)!] = JSON5.parse(rawValue); }
  catch { target[keys.at(-1)!] = rawValue; }
  await writeConfig(paths, config);
  print({ ok: true });
});
configCommand.command("validate").action(async () => {
  const paths = cliPaths();
  const errors = validateConfig(await loadConfig(paths));
  print({ ok: errors.length === 0, errors });
  if (errors.length) process.exitCode = 1;
});

const plugins = program.command("plugins");
plugins.command("list", { isDefault: true }).action(async () => {
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try { print({ allowed: context.config.plugins.allow, loaded: context.loadedPlugins, plugins: context.sharedStore.listPlugins() }); }
  finally { await context.close(); }
});
plugins.command("inspect").argument("<package>").action(async (packageName) => {
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try { print({ manifest: await context.pluginManager.inspect(packageName) }); }
  finally { await context.close(); }
});
plugins.command("install").argument("<package>").option("--enable", "enable and allow the plugin immediately").action(async (packageName, options) => {
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try {
    const manifest = await context.pluginManager.install(packageName, Boolean(options.enable));
    if (options.enable && !context.config.plugins.allow.includes(manifest.id)) {
      context.config.plugins.allow.push(manifest.id);
      await writeConfig(context.paths, context.config);
    }
    print({ installed: manifest.id, enabled: Boolean(options.enable), manifest });
  } finally { await context.close(); }
});
plugins.command("enable").argument("<id>").action(async (id) => {
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try {
    if (!context.sharedStore.setPluginEnabled(id, true)) throw new Error(`Unknown plugin: ${id}`);
    if (!context.config.plugins.allow.includes(id)) context.config.plugins.allow.push(id);
    await writeConfig(context.paths, context.config);
    print({ id, enabled: true, restartRequired: true });
  } finally { await context.close(); }
});
plugins.command("disable").argument("<id>").action(async (id) => {
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try {
    if (!context.sharedStore.setPluginEnabled(id, false)) throw new Error(`Unknown plugin: ${id}`);
    context.config.plugins.allow = context.config.plugins.allow.filter((entry) => entry !== id);
    await writeConfig(context.paths, context.config);
    print({ id, enabled: false, restartRequired: true });
  } finally { await context.close(); }
});
plugins.command("uninstall").argument("<id>").action(async (id) => {
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try {
    if (!(await context.pluginManager.uninstall(id))) throw new Error(`Unknown plugin: ${id}`);
    context.config.plugins.allow = context.config.plugins.allow.filter((entry) => entry !== id);
    await writeConfig(context.paths, context.config);
    print({ id, uninstalled: true });
  } finally { await context.close(); }
});
plugins.command("manage").description("interactively install, inspect, enable, and remove plugins").action(async () => { await managePluginsTui(); });

const approvals = program.command("approvals");
approvals.command("list", { isDefault: true }).option("--status <status>", "filter by status", "pending").action(async (options) => {
  print(await withClient((client) => client.request("approvals.list", { status: options.status })));
});
for (const [command, decision] of [["approve", "approved"], ["deny", "denied"]] as const) {
  approvals.command(command).argument("<id>").action(async (id) => {
    print(await withClient((client) => client.request("approvals.resolve", { id, decision })));
  });
}
approvals.command("review").description("interactively review pending consequential actions").action(async () => { await reviewApprovalsTui(); });

program.command("doctor").description("validate the local headless installation")
  .option("--production", "require production state, authentication, recovery tooling, and security checks")
  .action(async (options) => {
  const paths = cliPaths();
  const production = Boolean(options.production);
  const checks: Array<{ name: string; ok: boolean; required: boolean; message: string }> = [];
  const version = process.versions.node.split(".").map(Number);
  const minimums: Record<number, [number, number]> = { 22: [22, 3], 24: [15, 0], 25: [9, 0] };
  const minimum = minimums[version[0] ?? -1];
  const supportedNode = Boolean(minimum && ((version[1] ?? 0) > minimum[0] || ((version[1] ?? 0) === minimum[0] && (version[2] ?? 0) >= minimum[1])));
  checks.push({ name: "node", ok: supportedNode, required: true, message: process.version });
  let config: Awaited<ReturnType<typeof loadConfig>> | undefined;
  try {
    config = await loadConfig(paths);
    checks.push({ name: "config", ok: true, required: true, message: `${config.gateway.bind}:${config.gateway.port}` });
  } catch (error) {
    checks.push({ name: "config", ok: false, required: true, message: (error as Error).message });
  }
  checks.push({ name: "headless", ok: true, required: true, message: "No Control UI or WebChat is shipped" });
  if (production) {
    const accessible = async (target: string) => fs.access(target).then(() => true, () => false);
    checks.push({ name: "state", ok: await accessible(paths.stateDir), required: true, message: paths.stateDir });
    checks.push({ name: "config-file", ok: await accessible(paths.configPath), required: true, message: paths.configPath });
    checks.push({ name: "master-key", ok: Boolean(process.env.NORI_MASTER_KEY) || await accessible(paths.masterKeyPath), required: true, message: process.env.NORI_MASTER_KEY ? "NORI_MASTER_KEY" : paths.masterKeyPath });
    try {
      await execFileAsync("tar", ["--version"], { timeout: 5_000 });
      checks.push({ name: "backup-tool", ok: true, required: true, message: "tar is available" });
    } catch (error) {
      checks.push({ name: "backup-tool", ok: false, required: true, message: (error as Error).message });
    }
    if (config) {
      const gatewayToken = process.env.NORI_GATEWAY_TOKEN ?? process.env.OPENCLAW_GATEWAY_TOKEN ?? await resolveSecret(config.gateway.auth.token).catch(() => undefined);
      checks.push({ name: "gateway-auth", ok: Boolean(gatewayToken), required: true, message: gatewayToken ? "configured" : "missing" });
      const model = config.agents.defaults.model.primary;
      checks.push({ name: "model", ok: Boolean(model), required: true, message: model || "missing" });
      if (model) {
        const providerId = model.split("/")[0]!;
        const provider = config.providers[providerId] ?? {};
        let providerAuth = false;
        let authMessage = "missing credential";
        const localEndpoint = (() => {
          if (["ollama", "lmstudio", "vllm"].includes(providerId)) return true;
          if (!provider.baseUrl) return false;
          try { return ["localhost", "127.0.0.1", "::1"].includes(new URL(provider.baseUrl).hostname) || new URL(provider.baseUrl).hostname.endsWith(".localhost"); }
          catch { return false; }
        })();
        if (localEndpoint) { providerAuth = true; authMessage = "local endpoint"; }
        else if (provider.authMode === "oauth" && providerId === "openai" && await accessible(agentDatabasePath(paths, "main"))) {
          const vault = new SecretVault(paths.masterKeyPath);
          await vault.initialize();
          const store = new AgentStore(agentDatabasePath(paths, "main"), vault);
          try {
            providerAuth = openAiOAuthStatus(store).authenticated;
            authMessage = providerAuth ? "OpenAI OAuth" : "OpenAI OAuth credential missing";
          } finally { store.close(); }
        } else {
          providerAuth = Boolean(await resolveSecret(provider.apiKey).catch(() => undefined));
          authMessage = providerAuth ? "API credential reference resolved" : "API credential reference missing or empty";
        }
        checks.push({ name: "provider-auth", ok: providerAuth, required: true, message: `${providerId}: ${authMessage}` });
      }
      for (const [channelId, channel] of Object.entries(config.channels)) {
        const accounts = "accounts" in channel && channel.accounts && typeof channel.accounts === "object" ? channel.accounts : { default: channel };
        for (const [accountId, account] of Object.entries(accounts)) {
          if (account.enabled === false || !account.token) continue;
          const available = Boolean(await resolveSecret(account.token).catch(() => undefined));
          checks.push({ name: `channel-auth:${channelId}/${accountId}`, ok: available, required: true, message: available ? "credential reference resolved" : "credential reference missing or empty" });
        }
      }
      const security = await auditSecurity(paths, config);
      checks.push({ name: "security", ok: security.summary.critical === 0, required: true, message: `${security.summary.critical} critical, ${security.summary.warning} warning` });
    }
  }
  const ok = checks.every((check) => !check.required || check.ok);
  print({ ok, productionReady: production ? ok : undefined, checks });
  if (!ok) process.exitCode = 1;
});

const backupCommand = program.command("backup").description("create and verify private local backup archives");
backupCommand.command("create", { isDefault: true })
  .option("--output <path>", "archive file or destination directory")
  .option("--dry-run", "show sources without writing an archive")
  .option("--verify", "verify the archive after creation")
  .option("--no-include-workspace", "exclude configured workspaces")
  .option("--only-config", "back up only nori.json")
  .action(async (options) => {
    const paths = cliPaths();
    await ensureNoriDirectories(paths);
    const config = await loadConfig(paths);
    print(await createBackup(paths, config, {
      ...(options.output ? { output: options.output } : {}),
      dryRun: Boolean(options.dryRun),
      verify: Boolean(options.verify),
      includeWorkspace: Boolean(options.includeWorkspace),
      onlyConfig: Boolean(options.onlyConfig),
      version: VERSION,
    }));
  });
backupCommand.command("verify").argument("<archive>").action(async (archive) => print(await verifyBackup(archive)));
backupCommand.command("restore").argument("<archive>")
  .option("--target <directory>", "restore into this directory instead of the active Nori state directory")
  .option("--apply", "perform the restore; the default is a dry run")
  .option("--force", "preserve and replace a non-empty target")
  .action(async (archive, options) => print(await restoreBackup(cliPaths(), archive, {
    ...(options.target ? { target: options.target } : {}),
    apply: Boolean(options.apply),
    force: Boolean(options.force),
  })));

const securityCommand = program.command("security").description("audit and fix common Nori security footguns");
securityCommand.command("audit", { isDefault: true }).option("--deep", "also probe the running Gateway").option("--fix", "apply deterministic permission and group-policy fixes")
  .action(async (options) => {
    const paths = cliPaths();
    await ensureNoriDirectories(paths);
    const config = await loadConfig(paths);
    const fix = options.fix ? await fixSecurity(paths, config) : undefined;
    const report = await auditSecurity(paths, config);
    let gatewayProbe: unknown;
    if (options.deep) {
      try { gatewayProbe = await withClient((client) => client.request("health")); }
      catch (error) { gatewayProbe = { ok: false, message: (error as Error).message }; }
    }
    print({ ...(fix ? { fix } : {}), report, ...(options.deep ? { gatewayProbe } : {}) });
    if (report.summary.critical) process.exitCode = 1;
  });

const updateCommand = program.command("update").description("check or update the installed Nori package")
  .option("--tag <tag-or-version>", "npm dist-tag or exact version")
  .option("--dry-run", "show the package-manager operation without running it")
  .option("--timeout <seconds>", "package-manager timeout", "1800")
  .option("--yes", "confirm package replacement")
  .action(async (options) => {
    if (!options.dryRun && !options.yes) throw new Error("Package replacement requires --yes; use --dry-run to preview");
    print(await runUpdate(VERSION, { ...(options.tag ? { tag: options.tag } : {}), dryRun: Boolean(options.dryRun), timeoutSeconds: Number(options.timeout) }));
  });
updateCommand.command("status", { isDefault: false }).option("--timeout <seconds>", "registry timeout", "3")
  .action(async (options) => print(await updateStatus(VERSION, { timeoutMs: Number(options.timeout) * 1_000 })));

program.command("migrate").argument("<source>", "migration source; currently openclaw").option("--source-dir <path>").option("--apply").option("--include-sessions").option("--include-credentials").action(async (source, options) => {
  if (source !== "openclaw") throw new Error(`Unsupported migration source: ${source}`);
  const context = await createContext(profile(), { loadPlugins: false, initializeMcp: false });
  try { print(await migrateOpenClaw(context.paths, context.agentStore, { source: options.sourceDir, apply: options.apply, includeSessions: options.includeSessions, includeCredentials: options.includeCredentials }, context.storeForAgent)); }
  finally { await context.close(); }
});

program.command("completion").addOption(new Option("--shell <shell>").choices(["bash", "zsh", "fish"]).default("bash")).action((options) => {
  const command = options.shell === "fish" ? "complete -c nori -a '(nori --help | string match -r \\\"^  [a-z][a-z-]+\\\" | string trim)'" : "complete -W \"menu interactive setup onboard agent tui chat terminal acp gateway daemon models memory mcp cron tasks channels message pairing devices nodes agents sessions transcripts tools skills hooks sandbox plugins approvals config status health logs doctor backup security update uninstall migrate completion\" nori";
  print(command);
});

program.showHelpAfterError();
program.configureOutput({ outputError: (message) => process.stderr.write(pc.red(message)) });

function isMainModule(): boolean {
  if (!process.argv[1]) return false;
  try { return import.meta.url === pathToFileURL(realpathSync(process.argv[1])).href; }
  catch { return false; }
}

if (isMainModule()) {
  const argv = process.argv.length === 2 && input.isTTY && output.isTTY ? [...process.argv, "menu"] : process.argv;
  program.parseAsync(argv).catch((error: Error) => {
    if (program.opts().json) print({ error: { code: (error as Error & { code?: string }).code ?? "CLI_ERROR", message: error.message } });
    else process.stderr.write(`${pc.red("Error:")} ${error.message}\n`);
    process.exitCode = 1;
  });
}

export { program, VERSION, PROTOCOL_VERSION };
